/* ──────────────────────────────────────────────────────────────────────────
 * Advisor Onboarding / Login — the single "Login as an Advisor" entry point.
 *
 * Public page (/advisor-onboarding) with a strict existing-vs-new gate:
 *   • EXISTING advisor → email + password → authenticated via /api/auth/login;
 *     proceeds ONLY if the account already has a registered advisor profile,
 *     then lands on the Advisor Marketplace. Otherwise a pop-up error is shown.
 *   • NEW advisor → email/mobile are de-duplicated across ALL platform users
 *     AND advisor profiles; on a clean check we create the account + advisor
 *     profile and land on the marketplace. Any duplicate raises a pop-up error.
 *
 * Logged-in visitors who already have an advisor profile are sent straight to
 * the marketplace; logged-in visitors without one skip account creation and go
 * straight to the registration form (their session is reused).
 * ────────────────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "./AdvisorOnboardingLogin.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyTrack = () => ({ project_name: "", exchange: "", issue_size: "", year: "", details: "" });
const emptyFee = () => ({ label: "", amount: "" });

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

// Icon + one-line descriptor per expertise area (keyed by backend area id).
const AREA_META = {
  ca_auditor: { icon: "ti-calculator", d: "Audit, restatement" },
  peer_review_auditor: { icon: "ti-search", d: "SEBI-mandated review" },
  tax_advisor: { icon: "ti-receipt-tax", d: "Direct & indirect tax" },
  cfo: { icon: "ti-wallet", d: "Finance leadership" },
  company_secretary: { icon: "ti-file-certificate", d: "Compliance, ROC" },
  legal_advisor: { icon: "ti-scale", d: "DRHP, due diligence" },
  independent_director: { icon: "ti-user-star", d: "Board, governance" },
  valuation_expert: { icon: "ti-coin", d: "Registered valuer" },
  merchant_banker: { icon: "ti-building-bank", d: "BRLM / lead manager" },
  underwriter: { icon: "ti-shield-check", d: "Issue underwriting" },
  rta: { icon: "ti-id", d: "Issue registry" },
  credit_rating: { icon: "ti-star", d: "Ratings & monitoring" },
  banker_to_issue: { icon: "ti-businessplan", d: "Escrow / refund banks" },
  advertising_agency: { icon: "ti-speakerphone", d: "IPO advertising" },
  market_maker: { icon: "ti-trending-up", d: "SME liquidity" },
};

const AdvisorOnboardingLogin = () => {
  const navigate = useNavigate();
  const [booting, setBooting] = useState(true);
  const [mode, setMode] = useState("gate"); // gate | existing | new
  const [authUser, setAuthUser] = useState(null);

  const [areas, setAreas] = useState([]);
  const [selected, setSelected] = useState([]);
  const [tier, setTier] = useState("prm");
  const [workLoc, setWorkLoc] = useState("pan_india");
  const [submitting, setSubmitting] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [dup, setDup] = useState({ open: false, title: "", msg: "", showRegister: false });
  const [login, setLogin] = useState({ email: "", password: "" });
  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "", city: "", state: "",
    pincode: "", years: "", regno: "", password: "", confirm: "",
  });
  const [track, setTrack] = useState([]);
  const [fees, setFees] = useState([]);

  const openDup = (title, msg, showRegister = false) => setDup({ open: true, title, msg, showRegister });

  useEffect(() => {
    fetch(`${API_URL}/api/matchmaker/expert/expertise-areas`)
      .then((r) => r.json())
      .then((d) => setAreas(d.areas || []))
      .catch(() => {});
  }, []);

  // Decide the initial mode based on the session: logged-in advisors go straight
  // to the marketplace; logged-in non-advisors register (session reused);
  // logged-out visitors get the existing-vs-new choice gate.
  useEffect(() => {
    let active = true;
    const ob = new URLSearchParams(window.location.search).get("ob");
    (async () => {
      try {
        const me = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
        if (!active) return;
        if (me.ok) {
          const u = await me.json().catch(() => null);
          setAuthUser(u);
          try {
            const p = await fetch(`${API_URL}/api/matchmaker/expert/my-profile`, { credentials: "include" });
            const pd = p.ok ? await p.json() : { profile: null };
            if (pd && pd.profile) {
              navigate("/advisor-marketplace");
              toast.info("You're already registered as an advisor — taking you to the marketplace.");
              return;
            }
          } catch { /* ignore */ }
          setMode("new"); // logged in, no advisor profile → register (no password)
          if (ob === "needprofile") toast.info("You're signed in with Google — complete your advisor registration below.");
        } else {
          // Logged out. Surface any Google sign-in outcome carried back via ?ob=.
          if (ob === "notfound") {
            setMode("existing");
            openDup("No account found", "We couldn't find an IPOLabs account for that Google email. Please register as a new advisor, or sign in with the email and password you registered with.", true);
          } else if (ob === "exists") {
            setMode("existing");
            openDup("Account already exists", "This Google account already exists. Please sign in to continue.");
          } else if (ob === "error") {
            setMode("existing");
            openDup("Google sign-in failed", "Something went wrong signing you in with Google. Please try again, or use your registered email and password.");
          } else {
            setMode("gate");
          }
        }
      } catch {
        if (active) setMode("gate");
      } finally {
        if (active) {
          setBooting(false);
          if (ob) window.history.replaceState(null, "", "/advisor-onboarding");
        }
      }
    })();
    return () => { active = false; };
  }, [navigate]);

  const homeDest = authUser ? "/home" : "/";

  // Advisor Google sign-in: reuse the platform OAuth flow, returning to this page
  // with intent=existing so a Google email with no account is rejected cleanly.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const r = `${window.location.origin}/advisor-onboarding?flow=existing&next=advisor`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(r)}`;
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setL = (k) => (e) => setLogin((l) => ({ ...l, [k]: e.target.value }));

  const toggle = (id) =>
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) { toast.error("Choose up to 3 areas of expertise."); return prev; }
      return [...prev, id];
    });

  // ── EXISTING advisor: authenticate, then require an advisor profile ────
  const loginExisting = async (e) => {
    if (e) e.preventDefault();
    if (loggingIn) return;
    const email = login.email.trim().toLowerCase();
    if (!email || !login.password) { toast.error("Enter your email and password."); return; }
    setLoggingIn(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: login.password, login_role: "existing_user" }),
      });
      if (!res.ok) {
        openDup(
          "Login failed",
          "The email or password is incorrect. Please check your details, or register as a new advisor.",
          true,
        );
        return;
      }
      // Must already have a registered advisor profile.
      let hasProfile = false;
      try {
        const p = await fetch(`${API_URL}/api/matchmaker/expert/my-profile`, { credentials: "include" });
        if (p.ok) { const pd = await p.json(); hasProfile = !!(pd && pd.profile); }
      } catch { /* ignore */ }
      if (!hasProfile) {
        setAuthUser({ email });
        openDup(
          "No advisor profile found",
          "This account isn't registered as an advisor yet. Please register as a new advisor to join the marketplace.",
          true,
        );
        return;
      }
      navigate("/advisor-marketplace");
      toast.success("Welcome back! Taking you to the Advisor Marketplace.");
    } catch {
      openDup("Login failed", "Something went wrong. Please try again in a moment.");
    } finally {
      setLoggingIn(false);
    }
  };

  // ── NEW advisor: de-dupe everywhere → create account (if guest) → profile ──
  const submit = async () => {
    if (submitting) return;
    if (!form.full_name.trim() || !form.email.trim() || !form.mobile.trim() || !form.city.trim() || !form.state.trim()) {
      toast.error("Please fill name, email, mobile, city and state.");
      return;
    }
    if (selected.length === 0) { toast.error("Select at least one area of expertise."); return; }

    const isGuest = !authUser;
    if (isGuest) {
      if (!form.password || form.password.length < 6) { toast.error("Create a password of at least 6 characters."); return; }
      if (form.password !== form.confirm) { toast.error("Passwords do not match."); return; }
    }

    setSubmitting(true);
    try {
      if (isGuest) {
        // 1) Strict duplicate check across ALL users + advisor profiles.
        const chk = await fetch(`${API_URL}/api/matchmaker/expert/check-availability`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: form.email.trim().toLowerCase(), mobile: form.mobile.trim() }),
        });
        const chkd = chk.ok ? await chk.json().catch(() => ({ available: true })) : { available: true };
        if (chkd && chkd.available === false) {
          openDup("Account already exists", chkd.message || "This email or mobile number is already registered.", false);
          setSubmitting(false); return;
        }
        // 2) Create the platform account (sets the session cookie).
        const reg = await fetch(`${API_URL}/api/auth/register`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: form.full_name.trim(), email: form.email.trim().toLowerCase(),
            mobile: form.mobile.trim(), password: form.password,
          }),
        });
        if (reg.status === 409) {
          openDup("Account already exists", "This email or mobile number is already registered. Please sign in as an existing advisor.", true);
          setSubmitting(false); return;
        }
        if (!reg.ok) { toast.error("Could not create your account. Please try again."); setSubmitting(false); return; }
      }

      // 3) Register the advisor profile (uses the session cookie).
      const fd = new FormData();
      fd.append("full_name", form.full_name.trim());
      fd.append("email", form.email.trim());
      fd.append("mobile", form.mobile.trim());
      fd.append("city", form.city.trim());
      fd.append("state", form.state.trim());
      fd.append("pincode", form.pincode.trim());
      const yrs = parseInt(form.years, 10) || 0;
      fd.append("ipo_experience", yrs > 0 ? "yes" : "no");
      fd.append("years_of_experience", String(yrs));
      fd.append("expertise_areas", JSON.stringify(selected));
      fd.append("work_location", workLoc);
      fd.append("track_record", JSON.stringify(track.filter((t) => (t.project_name || "").trim())));
      fd.append("indicative_fees", JSON.stringify(fees.filter((f) => (f.label || "").trim() && (f.amount || "").trim())));

      const res = await fetch(`${API_URL}/api/matchmaker/expert/register`, {
        method: "POST", credentials: "include", body: fd,
      });

      if (res.status === 400) {
        navigate("/advisor-marketplace");
        toast.info("You're already registered as an advisor — taking you to the marketplace.");
        return;
      }
      if (res.status === 409) {
        openDup(
          "Account already exists",
          "The email or mobile number is already in use by another advisor account. Each advisor can register only once — please sign in, or use a different email and mobile number.",
          true,
        );
        return;
      }
      if (!res.ok) { toast.error("Could not register. Please try again."); return; }

      if (tier === "prm") {
        // Premium is a paid upgrade — send them to complete real Razorpay checkout.
        toast.success("Profile created! Complete your Premium upgrade to get featured.");
        navigate("/advisor-premium");
        return;
      }
      navigate("/advisor-marketplace");
      toast.success("Welcome aboard! Your profile is live. Credentials will be verified against ICAI / ICSI / SEBI / MCA.");
    } catch (e) {
      toast.error(e.message || "Could not register. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isGuest = !authUser;

  return (
    <div className="adv-login-root" data-testid="advisor-onboarding-page">
      <div className="al-topbar">
        <div className="al-left">
          <div className="al-brand" onClick={() => navigate(homeDest)} data-testid="al-brand">
            IPO<span>Navigator</span>
          </div>
          <button className="al-navbtn" onClick={() => navigate(homeDest)} data-testid="al-home-btn" title="Home Page">
            <i className="ti ti-home" aria-hidden="true" /><span className="al-navbtn-t">Home Page</span>
          </button>
          <button className="al-navbtn oracle" onClick={() => navigate("/oracle")} data-testid="al-ask-oracle" title="Ask Oracle {IPO.GPT}">
            <i className="ti ti-robot" aria-hidden="true" /><span className="al-navbtn-t">Ask Oracle {"{IPO.GPT}"}</span>
          </button>
        </div>
        <div className="al-eyebrow">Advisor Onboarding</div>
        <div className="al-back" onClick={() => navigate(homeDest)} data-testid="al-back">
          <i className="ti ti-arrow-left" aria-hidden="true" /> Back to home
        </div>
      </div>

      <div className="al-shell">
        <div className="ad">
          {booting && (
            <div className="al-boot" data-testid="al-boot">
              <div className="al-spinner" /> Loading…
            </div>
          )}

          {/* ── GATE: existing vs new ─────────────────────────────────── */}
          {!booting && mode === "gate" && (
            <>
              <div className="ad-head">
                <div className="ad-kick">Advisor marketplace · Login or join</div>
                <div className="ad-h">Login as an Advisor</div>
                <div className="ad-sub">
                  Choose how you'd like to continue. Existing advisors sign in with their email and
                  password; new advisors register their verified IPO expertise to join the marketplace.
                </div>
              </div>

              <div className="al-gate" data-testid="al-gate">
                <button className="al-gcard existing" onClick={() => setMode("existing")} data-testid="al-choose-existing">
                  <div className="al-gic"><i className="ti ti-login" aria-hidden="true" /></div>
                  <div className="al-gt">I'm already an advisor</div>
                  <div className="al-gd">Sign in with your registered email and password to reach the Advisor Marketplace.</div>
                  <span className="al-gcta">Log in <i className="ti ti-arrow-right" aria-hidden="true" /></span>
                </button>
                <button className="al-gcard new" onClick={() => setMode("new")} data-testid="al-choose-new">
                  <div className="al-gic"><i className="ti ti-user-plus" aria-hidden="true" /></div>
                  <div className="al-gt">I'm new here</div>
                  <div className="al-gd">Register your credentials and subject-matter expertise to get matched with MSME IPO mandates.</div>
                  <span className="al-gcta">Register as an advisor <i className="ti ti-arrow-right" aria-hidden="true" /></span>
                </button>
              </div>
            </>
          )}

          {/* ── EXISTING advisor login ────────────────────────────────── */}
          {!booting && mode === "existing" && (
            <>
              <div className="ad-head">
                <div className="ad-kick">Advisor marketplace · Sign in</div>
                <div className="ad-h">Welcome back, advisor</div>
                <div className="ad-sub">
                  Enter the email and password you registered with. We'll take you straight to the
                  Advisor Marketplace once your details match.
                </div>
              </div>

              <div className="ad-card">
                <div className="sec-t"><i className="ti ti-login" aria-hidden="true" /> Advisor sign in</div>
                <form onSubmit={loginExisting}>
                  <div className="fg">
                    <label className="fl">Registered email</label>
                    <input className="fi" type="email" placeholder="you@firm.com" value={login.email} onChange={setL("email")} data-testid="al-login-email" />
                  </div>
                  <div className="fg">
                    <label className="fl">Password</label>
                    <input className="fi" type="password" placeholder="Your password" value={login.password} onChange={setL("password")} data-testid="al-login-password" />
                  </div>
                  <div className="ad-foot">
                    <span className="al-forgot" onClick={() => navigate("/login")} data-testid="al-forgot">Forgot password?</span>
                    <button className="submit" type="submit" disabled={loggingIn} data-testid="al-login-submit">
                      {loggingIn ? "Signing in…" : "Log in →"}
                    </button>
                  </div>
                </form>

                <div className="al-or"><span>or</span></div>
                <button type="button" className="al-google" onClick={googleLogin} data-testid="al-login-google">
                  <GoogleIcon /> Sign in with Google
                </button>
                <div className="al-google-hint">
                  <i className="ti ti-info-circle" aria-hidden="true" /> Use the same Google account linked to the email you registered with.
                </div>

                <div className="al-switch">
                  New here? <a onClick={() => setMode("new")} data-testid="al-switch-to-new">Register as a new advisor</a>
                </div>
              </div>
            </>
          )}

          {/* ── NEW advisor registration ──────────────────────────────── */}
          {!booting && mode === "new" && (
            <>
              <h2 className="sr-only">
                Advisor onboarding — register identity, SEBI/ICAI credentials, subject-matter expertise,
                work-location preference, and a premium vs standard listing tier.
              </h2>

              <div className="ad-head">
                <div className="ad-kick">Advisor marketplace · Join as a professional</div>
                <div className="ad-h">Register your IPO expertise</div>
                <div className="ad-sub">
                  List your verified credentials and get matched to MSMEs assembling their IPO teams. We
                  verify against ICAI, ICSI, SEBI and MCA before your profile is marked verified.
                </div>
              </div>

              <div className="ad-card">
                <div className="sec-t"><i className="ti ti-user-check" aria-hidden="true" /> Identity &amp; credentials</div>
                <div className="row2">
                  <div className="fg"><label className="fl">Full name / firm name</label><input className="fi" placeholder="e.g. S. R. Associates LLP" value={form.full_name} onChange={set("full_name")} data-testid="al-name" /></div>
                  <div className="fg"><label className="fl">Work email</label><input className="fi" placeholder="you@firm.com" value={form.email} onChange={set("email")} data-testid="al-email" /></div>
                </div>
                <div className="row2">
                  <div className="fg"><label className="fl">Mobile number</label><input className="fi" placeholder="e.g. 98xxxxxxxx" value={form.mobile} onChange={set("mobile")} data-testid="al-mobile" /></div>
                  <div className="fg"><label className="fl">Years of IPO experience</label><input className="fi" placeholder="e.g. 8" value={form.years} onChange={set("years")} data-testid="al-years" /></div>
                </div>
                {isGuest && (
                  <div className="row2" data-testid="al-password-section">
                    <div className="fg"><label className="fl">Create a password</label><input className="fi" type="password" placeholder="At least 6 characters" value={form.password} onChange={set("password")} data-testid="al-password" /></div>
                    <div className="fg"><label className="fl">Confirm password</label><input className="fi" type="password" placeholder="Re-enter password" value={form.confirm} onChange={set("confirm")} data-testid="al-confirm" /></div>
                  </div>
                )}
                <div className="row2">
                  <div className="fg"><label className="fl">City</label><input className="fi" placeholder="e.g. Pune" value={form.city} onChange={set("city")} data-testid="al-city" /></div>
                  <div className="fg"><label className="fl">State</label><input className="fi" placeholder="e.g. Maharashtra" value={form.state} onChange={set("state")} data-testid="al-state" /></div>
                </div>
                <div className="row2">
                  <div className="fg"><label className="fl">PIN code</label><input className="fi" placeholder="e.g. 411001" value={form.pincode} onChange={set("pincode")} data-testid="al-pin" /></div>
                  <div className="fg"><label className="fl">Membership / registration no.</label><input className="fi" placeholder="ICAI / ICSI / SEBI reg." value={form.regno} onChange={set("regno")} data-testid="al-regno" /></div>
                </div>

                <div className="sec-t" style={{ marginTop: 6 }}><i className="ti ti-briefcase" aria-hidden="true" /> Your subject-matter expertise <span style={{ textTransform: "none", letterSpacing: 0, color: "#9BA3AD" }}>· pick up to 3</span></div>
                <div className="exp-grid" data-testid="al-expertise-grid">
                  {areas.map((a) => {
                    const meta = AREA_META[a.id] || { icon: "ti-point", d: "" };
                    return (
                      <button
                        key={a.id}
                        className={`exp${selected.includes(a.id) ? " on" : ""}`}
                        onClick={() => toggle(a.id)}
                        data-testid={`al-exp-${a.id}`}
                      >
                        <div className="exp-h"><i className={`ti ${meta.icon}`} aria-hidden="true" /> {a.label}</div>
                        <div className="exp-d">{meta.d}</div>
                      </button>
                    );
                  })}
                </div>

                <div className="sec-t" style={{ marginTop: 10 }}><i className="ti ti-map-pin" aria-hidden="true" /> Where will you take up work?</div>
                <div className="tier-row" data-testid="al-worklocation">
                  <button className={`tier${workLoc === "city_only" ? " on" : ""}`} onClick={() => setWorkLoc("city_only")} data-testid="al-work-city">
                    <div className="tier-top"><i className="ti ti-building-community" aria-hidden="true" /><span className="tier-n">My city only</span></div>
                    <div className="tier-p">In-person · local mandates</div>
                    <div className="tier-f">I prefer to work with issuers based in my city / region for face-to-face engagement.</div>
                  </button>
                  <button className={`tier${workLoc === "pan_india" ? " on" : ""}`} onClick={() => setWorkLoc("pan_india")} data-testid="al-work-panindia">
                    <div className="tier-top"><i className="ti ti-world" aria-hidden="true" /><span className="tier-n">Pan-India · online</span></div>
                    <div className="tier-p">Remote · video meetings</div>
                    <div className="tier-f">I take up mandates anywhere in India and work via online meetings &amp; secure document sharing.</div>
                  </button>
                </div>

                <div className="sec-t" style={{ marginTop: 10 }}><i className="ti ti-briefcase" aria-hidden="true" /> Track record — recent IPOs <span style={{ textTransform: "none", letterSpacing: 0, color: "#9BA3AD" }}>· optional</span></div>
                <div className="al-optlist" data-testid="al-track-list">
                  {track.map((t, i) => (
                    <div className="al-optrow" key={i} data-testid={`al-track-row-${i}`}>
                      <div className="fg"><input className="fi" placeholder="Project / company name" value={t.project_name} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, project_name: e.target.value } : x))} data-testid={`al-track-name-${i}`} /></div>
                      <div className="row3">
                        <input className="fi" placeholder="Exchange (e.g. BSE SME)" value={t.exchange} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, exchange: e.target.value } : x))} data-testid={`al-track-exchange-${i}`} />
                        <input className="fi" placeholder="Issue size (e.g. ₹28 Cr)" value={t.issue_size} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, issue_size: e.target.value } : x))} data-testid={`al-track-size-${i}`} />
                        <input className="fi" placeholder="Year (e.g. 2025)" value={t.year} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, year: e.target.value } : x))} data-testid={`al-track-year-${i}`} />
                      </div>
                      <div className="al-optrow-b">
                        <input className="fi" placeholder="Short details (e.g. restated financials + audit)" value={t.details} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, details: e.target.value } : x))} data-testid={`al-track-details-${i}`} />
                        <button type="button" className="al-optremove" onClick={() => setTrack((s) => s.filter((_, j) => j !== i))} data-testid={`al-track-remove-${i}`} aria-label="Remove project"><i className="ti ti-x" aria-hidden="true" /></button>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="al-optadd" onClick={() => setTrack((s) => [...s, emptyTrack()])} data-testid="al-track-add"><i className="ti ti-plus" aria-hidden="true" /> Add IPO project</button>
                </div>

                <div className="sec-t" style={{ marginTop: 10 }}><i className="ti ti-coin" aria-hidden="true" /> Indicative fees <span style={{ textTransform: "none", letterSpacing: 0, color: "#9BA3AD" }}>· optional</span></div>
                <div className="al-optlist" data-testid="al-fees-list">
                  {fees.map((f, i) => (
                    <div className="al-feerow" key={i} data-testid={`al-fee-row-${i}`}>
                      <input className="fi" placeholder="Fee item (e.g. Statutory audit & restatement)" value={f.label} onChange={(e) => setFees((s) => s.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} data-testid={`al-fee-label-${i}`} />
                      <input className="fi" placeholder="Amount (e.g. ₹6.5L)" value={f.amount} onChange={(e) => setFees((s) => s.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} data-testid={`al-fee-amount-${i}`} />
                      <button type="button" className="al-optremove" onClick={() => setFees((s) => s.filter((_, j) => j !== i))} data-testid={`al-fee-remove-${i}`} aria-label="Remove fee"><i className="ti ti-x" aria-hidden="true" /></button>
                    </div>
                  ))}
                  <button type="button" className="al-optadd" onClick={() => setFees((s) => [...s, emptyFee()])} data-testid="al-fee-add"><i className="ti ti-plus" aria-hidden="true" /> Add fee row</button>
                </div>

                <div className="sec-t" style={{ marginTop: 10 }}><i className="ti ti-star" aria-hidden="true" /> Choose your listing plan</div>
                <div className="tier-row">
                  <button className={`tier${tier === "std" ? " on" : ""}`} onClick={() => setTier("std")} data-testid="al-tier-std">
                    <div className="tier-top"><i className="ti ti-user" aria-hidden="true" /><span className="tier-n">Standard</span><span className="badge" style={{ background: "#F1EFE8", color: "#5F5E5A" }}>Free</span></div>
                    <div className="tier-p">₹0 · list &amp; receive inbound</div>
                    <div className="tier-f">Verified profile, basic listing, inbound mandate requests, ratings.</div>
                  </button>
                  <button className={`tier${tier === "prm" ? " on" : ""}`} onClick={() => setTier("prm")} data-testid="al-tier-prm">
                    <div className="tier-top"><i className="ti ti-crown" aria-hidden="true" /><span className="tier-n">Premium</span><span className="badge" style={{ background: "#FAEEDA", color: "#854F0B" }}>Featured</span></div>
                    <div className="tier-p">Subscription · priority placement</div>
                    <div className="tier-f">Top of search, proximity boost, proactive mandate matching, analytics, verified badge.</div>
                  </button>
                </div>

                <div className="ad-foot">
                  <span className="note"><i className="ti ti-shield-check" style={{ fontSize: 13, color: "#0F6E56" }} aria-hidden="true" /> Credentials verified against ICAI / ICSI / SEBI / MCA before your profile is marked verified.</span>
                  <button className="submit" onClick={submit} disabled={submitting} data-testid="al-submit">
                    {submitting ? "Submitting…" : "Join the marketplace →"}
                  </button>
                </div>

                <div className="al-switch">
                  Already an advisor? <a onClick={() => setMode("existing")} data-testid="al-switch-to-existing">Log in instead</a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {dup.open && (
        <div className="al-dup-overlay" data-testid="al-dup-overlay" onClick={() => setDup((d) => ({ ...d, open: false }))}>
          <div className="al-dup-modal" role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()} data-testid="al-dup-modal">
            <div className="al-dup-ic"><i className="ti ti-alert-triangle" aria-hidden="true" /></div>
            <div className="al-dup-h" data-testid="al-dup-title">{dup.title || "Account already exists"}</div>
            <p className="al-dup-d" data-testid="al-dup-message">{dup.msg}</p>
            <div className="al-dup-actions">
              {dup.showRegister && (
                <button
                  className="al-dup-btn ghost"
                  onClick={() => { setDup((d) => ({ ...d, open: false })); setMode("new"); }}
                  data-testid="al-dup-register"
                >
                  Register as new advisor
                </button>
              )}
              <button className="al-dup-btn" onClick={() => setDup((d) => ({ ...d, open: false }))} data-testid="al-dup-ok">Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvisorOnboardingLogin;
